import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import { ProjectContext } from "context/ProjectProvider";
import {
  InfoFeatureType,
  TAuditChangeLog
} from "change-events/change-event-polling";
import { MAX_NUMBER_OF_FETCH_ITEM } from "constants/index";
import { FeatureTypes } from "entity-app/constants";
import { getFeatureEntityLog } from "services/feature-apis";
import { toDependencyModel } from "pages/design-package-details-page/components/view-dependencies/adapter";
import { DependencyModel } from "pages/design-package-details-page/components/view-dependencies/model";
import {
  RawDependencyPayload,
  RawInstance,
  RawLink,
  RawMilestone
} from "pages/design-package-details-page/components/view-dependencies/raw-model";
import { toWorkspaceModel } from "../adapter";
import { WorkspaceModel } from "../model";

type RawDesignPackage = NonNullable<
  RawDependencyPayload["feature_instance_by_pk"]
>;

export type PlannerLogValue = {
  detailsById: Record<string, DependencyModel>;
  loading: boolean;
  refetch: () => Promise<void>;
};

const PlannerLogContext = createContext<PlannerLogValue | null>(null);

export function PlannerLogProvider({
  value,
  children
}: {
  value: PlannerLogValue;
  children: ReactNode;
}) {
  return createElement(PlannerLogContext.Provider, { value }, children);
}

function rowsFromLogResponse(data: unknown): RawDesignPackage[] {
  if (!Array.isArray(data)) return [];
  return data;
}

function flattenIdLists(nested: Array<string[] | undefined | null>): string[] {
  return [
    ...new Set(
      ([] as string[]).concat(...(nested.filter(Boolean) as string[][]))
    )
  ];
}

function mergeLogRows(
  prev: RawDesignPackage[],
  updated: RawDesignPackage[]
): RawDesignPackage[] {
  const items = [...prev];
  updated.forEach((item) => {
    if (!item?.id) return;
    const foundIndex = items.findIndex((row) => row?.id === item.id);
    if (foundIndex === -1) items.push(item);
    else items[foundIndex] = item;
  });
  return items;
}

const DELETE_CHANGE_TYPES = new Set(["delete", "bulk_delete"]);

// A new bid-package or planning-item link is not in the loaded graph yet, so
// these types always reload the planner. Materials, submittals, and activities
// reload only when the event names something already linked.
const ALWAYS_RELATED_FEATURE_TYPES = new Set<string>([
  InfoFeatureType.bidPackage,
  InfoFeatureType.planningActivity,
  InfoFeatureType.planningMaterial,
  InfoFeatureType.planningSubmittal
]);

const ALWAYS_RELATED_FEATURE_TYPE_IDS = new Set<number>([
  FeatureTypes.BID_PACKAGE,
  FeatureTypes.PLANNING_ONLY_ACTIVITY,
  FeatureTypes.PLANNING_ONLY_MATERIAL,
  FeatureTypes.PLANNING_ONLY_SUBMITTAL
]);

function changeTypeOf(log: TAuditChangeLog): string {
  return (log.change_type ?? "").toLowerCase();
}

function isDesignPackageLabel(value: unknown): boolean {
  if (value == null || value === "") return false;
  const designPackageLabel: string = InfoFeatureType.designPackage;
  const text = String(value).toUpperCase();
  return text === designPackageLabel || text === "5";
}

function isDirectDesignPackageChange(log: TAuditChangeLog): boolean {
  const type = log.info?.feature_type;
  const typeIds = log.info?.feature_type_ids;
  const designPackageLabel: string = InfoFeatureType.designPackage;
  const designPackageType: number = FeatureTypes.DESIGN_PACKAGE;
  const isDesignPackageRow =
    type === designPackageLabel ||
    (!type && !!typeIds?.includes(designPackageType));
  if (!isDesignPackageRow) return false;
  // Date and link writes land on other tables. Only the design-package row
  // itself is safe to patch by id.
  const source = log.data_source;
  return !source || source === "feature_instance";
}

function mentionsAlwaysRelatedType(log: TAuditChangeLog): boolean {
  const type = log.info?.feature_type;
  if (type && ALWAYS_RELATED_FEATURE_TYPES.has(type)) return true;
  return !!log.info?.feature_type_ids?.some((id) =>
    ALWAYS_RELATED_FEATURE_TYPE_IDS.has(id)
  );
}

function eventIds(log: TAuditChangeLog): string[] {
  const refreshIds = Array.isArray(log.info?.refreshList)
    ? log.info.refreshList.filter(
        (item): item is string => typeof item === "string"
      )
    : [];
  return [
    ...(log.info?.feature_instance_ids ?? []),
    ...(log.info?.material_ids ?? []),
    ...(log.info?.submittal_ids ?? []),
    ...(log.info?.linked_features ?? []).map((feature) => feature.id),
    ...refreshIds,
    log.object_id
  ].filter((id): id is string => Boolean(id));
}

function namesDesignPackage(log: TAuditChangeLog): boolean {
  const designPackageLabel: string = InfoFeatureType.designPackage;
  const designPackageType: number = FeatureTypes.DESIGN_PACKAGE;
  if (log.info?.feature_type === designPackageLabel) return true;
  if (log.info?.feature_type_ids?.includes(designPackageType)) return true;
  return (log.info?.linked_features ?? []).some((feature) =>
    isDesignPackageLabel(feature?.featureType)
  );
}

function addId(ids: Set<string>, id?: string | null) {
  if (id) ids.add(id);
}

function addMilestone(ids: Set<string>, milestone: RawMilestone) {
  addId(ids, milestone.id);
  milestone.feature_workflow_instance_offsets_as_src?.forEach((offset) =>
    addId(ids, offset.id)
  );
}

function addLink(ids: Set<string>, link: RawLink) {
  addId(ids, link.id);
  addId(ids, link.source_instance_milestone_id);
  addId(ids, link.source_feature_milestone?.id);
  if (link.target_feature_instance)
    addInstance(ids, link.target_feature_instance);
}

function addInstance(ids: Set<string>, instance: RawInstance) {
  addId(ids, instance.id);
  addId(ids, instance.feature_workflow_instance?.id);
  instance.workflow_instance_milestones?.forEach((milestone) =>
    addMilestone(ids, milestone)
  );
  instance.workflow_instance_offsets?.forEach((offset) =>
    addId(ids, offset.id)
  );
  instance.linkedSubmittals?.forEach((link) => addLink(ids, link));
  instance.linkedMaterials?.forEach((link) => addLink(ids, link));
  instance.linkedActivities?.forEach((link) => addLink(ids, link));
}

/** Ids already on the planner: the package, its links, and their date blocks. */
function collectPlannerIds(rows: RawDesignPackage[]): Set<string> {
  const ids = new Set<string>();
  rows.forEach((pkg) => {
    addId(ids, pkg.id);
    addId(ids, pkg.feature_workflow_instance?.id);
    pkg.workflow_instance_milestones?.forEach((milestone) =>
      addMilestone(ids, milestone)
    );
    pkg.linkedBidPackages?.forEach((link) => addLink(ids, link));
  });
  return ids;
}

/**
 * Audit rows that can change the planner:
 * - the design package row
 * - a bid-package or planning-item link (including a package not loaded yet)
 * - a date or field change on anything already linked (`linked_features`,
 *   `feature_instance_ids`, `object_id`, material/submittal ids)
 * - a `feature_linking` row
 */
function logAffectsPlanner(
  log: TAuditChangeLog,
  plannerIds: Set<string>
): boolean {
  if (log.data_source === "feature_linking") return true;
  if (isDirectDesignPackageChange(log)) return true;
  if (namesDesignPackage(log)) return true;
  if (mentionsAlwaysRelatedType(log)) return true;
  return eventIds(log).some((id) => plannerIds.has(id));
}

function syncPlannerRowsFromAudit(input: {
  eventLogs: TAuditChangeLog[] | undefined;
  previous: TAuditChangeLog[] | undefined;
  rows: RawDesignPackage[];
  refetch: () => Promise<void>;
  deleteEntitiesFromList: (ids: string[]) => void;
  getAndUpdateEntityList: (ids: string[]) => void;
}): void {
  const {
    eventLogs,
    previous,
    rows,
    refetch,
    deleteEntitiesFromList,
    getAndUpdateEntityList
  } = input;
  if (!eventLogs?.length || previous === eventLogs) return;
  const plannerIds = collectPlannerIds(rows);
  const featureLogs = eventLogs.filter((log) =>
    logAffectsPlanner(log, plannerIds)
  );
  if (!featureLogs.length) return;
  if (featureLogs.some((log) => !isDirectDesignPackageChange(log))) {
    refetch();
    return;
  }
  const deletedIds = flattenIdLists(
    featureLogs
      .filter((log) => DELETE_CHANGE_TYPES.has(changeTypeOf(log)))
      .map((log) => log.info?.feature_instance_ids)
  );
  if (deletedIds.length) deleteEntitiesFromList(deletedIds);
  const ids = flattenIdLists(
    featureLogs
      .filter((log) => !DELETE_CHANGE_TYPES.has(changeTypeOf(log)))
      .map((log) => log.info?.feature_instance_ids)
  );
  if (ids.length) getAndUpdateEntityList(ids);
}

export function useWorkspaceData(projectId: string, refreshKey = 0) {
  const { projectToken, tokenRetrievalState, eventLogs } =
    useContext(ProjectContext);
  const token = tokenRetrievalState?.token || projectToken;
  const canFetch = Boolean(token && projectId);

  const [raw, setRaw] = useState<RawDesignPackage[]>([]);
  const [loading, setLoading] = useState(canFetch);
  const [error, setError] = useState<Error | undefined>();
  const initialFetchDone = useRef(false);

  const refetch = useCallback(async () => {
    if (!token || !projectId) {
      setRaw([]);
      setLoading(false);
      setError(undefined);
      return;
    }
    setLoading(true);
    setError(undefined);
    try {
      const res = await getFeatureEntityLog({
        projectToken: token,
        featureType: FeatureTypes.DESIGN_PACKAGE
      });
      if (res.success && Array.isArray(res.data)) {
        setRaw(rowsFromLogResponse(res.data));
      } else {
        setRaw([]);
        setError(new Error(res.errorMsg || "Couldn't load the planner"));
      }
    } catch (err) {
      setRaw([]);
      setError(
        err instanceof Error ? err : new Error("Couldn't load the planner")
      );
    } finally {
      initialFetchDone.current = true;
      setLoading(false);
    }
  }, [token, projectId]);

  useEffect(() => {
    refetch();
  }, [refetch, refreshKey]);

  const getAndUpdateEntityList = useCallback(
    async (featureInstanceIds: string[]) => {
      if (!token || !featureInstanceIds.length) return;
      if (featureInstanceIds.length >= MAX_NUMBER_OF_FETCH_ITEM) {
        await refetch();
        return;
      }
      const res = await getFeatureEntityLog({
        projectToken: token,
        featureType: FeatureTypes.DESIGN_PACKAGE,
        feature_instance_ids: featureInstanceIds
      });
      if (!res?.success || !Array.isArray(res.data)) return;
      const updated = rowsFromLogResponse(res.data);
      setRaw((prev) => mergeLogRows(prev, updated));
    },
    [token, refetch]
  );

  const deleteEntitiesFromList = useCallback((ids: string[]) => {
    if (!ids.length) return;
    const remove = new Set(ids);
    setRaw((prev) => prev.filter((row) => !row?.id || !remove.has(row.id)));
  }, []);

  const previousEventLogs = useRef(eventLogs);
  const rawRef = useRef(raw);
  rawRef.current = raw;
  useEffect(() => {
    if (!initialFetchDone.current) {
      previousEventLogs.current = eventLogs;
      return;
    }
    syncPlannerRowsFromAudit({
      eventLogs,
      previous: previousEventLogs.current,
      rows: rawRef.current,
      refetch,
      deleteEntitiesFromList,
      getAndUpdateEntityList
    });
    previousEventLogs.current = eventLogs;
  }, [deleteEntitiesFromList, eventLogs, getAndUpdateEntityList, refetch]);

  const model: WorkspaceModel = useMemo(() => toWorkspaceModel(raw), [raw]);

  const detailsById: Record<string, DependencyModel> = useMemo(() => {
    const map: Record<string, DependencyModel> = {};
    raw.forEach((pkg) => {
      if (!pkg?.id) return;
      map[pkg.id] = toDependencyModel({ feature_instance_by_pk: pkg });
    });
    return map;
  }, [raw]);

  return { model, detailsById, loading, error, refetch };
}

// Looks up the already-fetched log row for this design package. Skipped until
// a dpId is provided (lanes still wait until they are in view). refetch reloads
// the whole project log — the same call useWorkspaceData made on mount.
export function useDesignPackageDetail(dpId: string | null) {
  const ctx = useContext(PlannerLogContext);
  const model: DependencyModel | null =
    dpId && ctx ? (ctx.detailsById[dpId] ?? null) : null;
  return {
    model,
    loading: ctx?.loading ?? false,
    error: undefined,
    refetch: ctx?.refetch ?? (() => Promise.resolve())
  };
}
